// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  applyTemplate,
  fetchCartConfigurations,
  selectCartUUID,
  type ProductType,
} from '@gandi/cart';
import { notifySuccess, notifyDanger } from '@gandi/notifications';
import {
  fetchOneOrganization,
  selectIsLoadedOrganizations,
} from '@gandi/redux-organization';
import {
  TemplateSelector,
  ToggleMode,
  type CollectionTemplate,
  type ToggleModeProps,
  type TemplateSelectorProps,
} from '@gandi/template2';
import { useTranslator } from '@gandi/translate';
import { Base, Text, Button } from '@gandi/ui';

import { getSetupOverviewUrl } from 'src/Cart/Setup/modules/helpers';
import { selectSetupOwner } from 'src/Cart/Setup/modules/selectors';
import type { CreateOrTransfer } from 'src/Cart/Setup/types';
import { Intro } from 'src/Layout/components/Intro';
import { Layout } from 'src/components/Layout';
import { useAppSelector } from 'src/hooks/useAppSelector';
import type { PreFetchReturn, PreFetchArgs, FullState } from 'src/types';

import Create from './components/Create';
import Edit, { type Props as EditProps } from './components/Edit';
import { getCurrentTemplate, getDefaultTemplateId } from './modules/actions';

type Params = {
  cartId: string,
};

type Query = {
  productType?: ProductType,
  process?: CreateOrTransfer,
};

const Templates = (): React.Node => {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartId } = useParams<Params>();
  const [searchParams] = useSearchParams();
  const currentCartUUID = useAppSelector(selectCartUUID);
  const [selectedTemplate, setSelectedTemplate] = React.useState();
  const [mode, setMode] = React.useState<ToggleModeProps['mode']>('select');
  // $FlowFixMe[incompatible-type] ProductType cannot be parsed: create a real product type as Array<string> to generate an union type ProductType
  const productType: null | ProductType = searchParams.get('productType');

  const process: void | CreateOrTransfer = ['create', 'transfer'].find(
    proc => proc === searchParams.get('process'),
  );

  const backUrl = getSetupOverviewUrl(cartId, process);

  // select default template according to current configured Id
  React.useEffect(
    () => {
      // the action getCurrentTemplate needs `fetchCartConfigurations` from
      // @gandi/cart which get cartId from state but when the cart has been
      // deleted this uuid is not set.
      // This is not a big problem since `Configure/index` will programmatically
      // redirect to overview because no products.
      // XXX: currentCartUUID check will be useless if this page is redirected
      // before 1st render
      if (process && productType && currentCartUUID) {
        // $FlowFixMe[incompatible-call]
        dispatch(getCurrentTemplate({ process, productType })).then(
          (template: void | CollectionTemplate) =>
            // $FlowFixMe[incompatible-call]
            setSelectedTemplate(template),
        );
      }
    },
    // exec on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleToggleMode: ToggleModeProps['onToggle'] = newMode => {
    setSelectedTemplate(undefined);
    setMode(newMode);
  };

  const handleSelectTemplate: TemplateSelectorProps['onChange'] = template => {
    setSelectedTemplate(template);
  };
  const handleSubmitSuccess: EditProps['onSubmitSuccess'] = async ({
    template,
  }) => {
    try {
      if (!process || !productType) {
        return;
      }

      await dispatch(
        applyTemplate({
          productType,
          process,
          templateId: template.id,
          templateName: template.name,
          templateDescription: template.description,
          noConflict: true,
        }),
      );

      await dispatch(fetchCartConfigurations());

      dispatch(
        notifySuccess(
          __('%(name)s template will be applied', {
            name: template.name,
          }),
        ),
      );

      navigate(backUrl);
    } catch (err) {
      dispatch(notifyDanger(__('Unable to apply the template')));
    }
  };

  const handleSubmitFail: EditProps['onSubmitFail'] = (
    error,
    disp,
    submitError,
  ) => {
    if (!error) {
      throw submitError;
    }

    dispatch(notifyDanger(__('Unable to save the template')));
  };

  if (!process || !productType) {
    return (
      <Layout>
        <Intro title={__('Configure your domains')} />
        <Base backgroundColor="surface" padding="small">
          <Text weight="semibold" tagName="p">
            {__('Unable to retrieve the current process or product.')}
          </Text>
          <Button to={backUrl} kind="outline">
            {__('Back')}
          </Button>
        </Base>
      </Layout>
    );
  }

  const processName =
    process === 'create' ? __('Registration') : __('Transfer');

  return (
    <Layout>
      <Intro title={`${__('Configure your domains')} — ${processName}`} />

      <Base marginBottom="base">
        <ToggleMode onToggle={handleToggleMode} mode={mode} />
      </Base>
      {mode === 'select' && (
        <>
          <Base marginBottom="base">
            <TemplateSelector
              onChange={handleSelectTemplate}
              value={selectedTemplate}
              context={process}
            />
          </Base>

          {selectedTemplate ? (
            <Edit
              onSubmitSuccess={handleSubmitSuccess}
              onSubmitFail={handleSubmitFail}
              selectedTemplate={selectedTemplate}
              process={process}
              backUrl={backUrl}
            />
          ) : (
            <Button grouped kind="outline" to={backUrl}>
              {__('Cancel')}
            </Button>
          )}
        </>
      )}
      {mode === 'create' && (
        <Create
          onSubmitSuccess={handleSubmitSuccess}
          onSubmitFail={handleSubmitFail}
          process={process}
          backUrl={backUrl}
        />
      )}
    </Layout>
  );
};

Templates.preFetch = (
  args: PreFetchArgs<{}, Params, Query, FullState>,
): PreFetchReturn<mixed> => {
  const { dispatch, getState, query } = args;
  const { productType } = query;

  const process: void | CreateOrTransfer = ['create', 'transfer'].includes(
    query.process,
  )
    ? query.process
    : undefined;
  const currentCartUUID = selectCartUUID(getState());

  if (!process || !productType || !currentCartUUID) {
    return Promise.resolve();
  }

  const setupOwner = selectSetupOwner(getState());

  // by default, fetch all necessary for edit
  return Promise.allSettled([
    !selectIsLoadedOrganizations(getState(), setupOwner) &&
      dispatch(fetchOneOrganization(setupOwner)),
    dispatch(getDefaultTemplateId(process)),
    dispatch(getCurrentTemplate({ process, productType })),
    // FIXME: prefetch with setupOwner when we are able to apply this strategy everywhere
    // $FlowFixMe[prop-missing] compat PreFetchArgs type
    // $FlowFixMe[incompatible-variance]
    TemplateSelector.preFetch(args),
  ]);
};

export default Templates;
